import { useMutation, useQueryClient } from '@tanstack/react-query';

import { secretKeys } from '@/features/secrets';
import { createConnection } from '@/lib/api/generated/api';
import type {
  ConnectionCreate,
  ConnectionView,
} from '@/lib/api/generated/model';

import { toConnectionError } from '../errors';
import { connectionKeys } from '../queryKeys';

/**
 * Connect a catalog provider. Takes the wire `ConnectionCreate` directly: the
 * Connect sheet builds `config` from the catalog's own field names, so there is
 * no form-values shape to translate. Invalidates the secrets list too, because
 * connecting writes the provider's managed entries.
 */
export function useConnectProvider(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConnectionCreate): Promise<ConnectionView> => {
      const response = await createConnection(workspaceId, data);
      if (response.status === 201) return response.data;
      throw toConnectionError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectionKeys.all(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: secretKeys.all(workspaceId),
      });
    },
  });
}
